const KNOWN_API_MESSAGES = {
  "Internal server error": "Błąd serwera. Spróbuj ponownie.",
  "Failed to fetch": "Brak połączenia z serwerem. Sprawdź internet i czy backend działa.",
  "Failed to fetch groups": "Nie udało się pobrać listy grup.",
  "Failed to fetch modules": "Nie udało się pobrać listy przedmiotów.",
  "Failed to fetch students": "Nie udało się pobrać listy studentów.",
  "Failed to fetch grades": "Nie udało się pobrać ocen.",
  "Failed to update grade": "Nie udało się zaktualizować oceny.",
  "Invalid grade format": "Niepoprawny format oceny.",
  "Network Error": "Błąd sieci. Sprawdź połączenie.",
};

/**
 * Funkcja do czyszczenia komunikatów błędów z wrażliwych informacji
 * Usuwa URL-e backendu, pełne ścieżki i inne wrażliwe dane
 */
export const sanitizeErrorMessage = (errorMessage) => {
  if (!errorMessage || typeof errorMessage !== 'string') {
    return 'Wystąpił nieoczekiwany błąd. Spróbuj ponownie.';
  }

  let cleaned = errorMessage;

  // Usuń pełne URL-e (http://, https://)
  cleaned = cleaned.replace(/https?:\/\/[^\s]+/gi, '');
  
  // Usuń nazwy domen backendu
  cleaned = cleaned.replace(/arrio-grade-book[^\s]*/gi, '');
  cleaned = cleaned.replace(/railway\.app[^\s]*/gi, '');
  cleaned = cleaned.replace(/localhost[^\s]*/gi, '');
  cleaned = cleaned.replace(/127\.0\.0\.1[^\s]*/gi, '');
  
  // Usuń ścieżki plików
  cleaned = cleaned.replace(/\/[^\s]+\.(js|ts|jsx|tsx)[^\s]*/gi, '');
  cleaned = cleaned.replace(/at\s+[^\s]+\s+\([^)]+\)/gi, '');
  
  // Usuń stack trace patterns
  cleaned = cleaned.replace(/Error:\s*/gi, '');
  cleaned = cleaned.replace(/at\s+/gi, '');
  
  // Usuń podwójne spacje i białe znaki
  cleaned = cleaned.replace(/\s+/g, ' ').trim();
  
  // Jeśli komunikat jest pusty lub za krótki, użyj domyślnego
  if (!cleaned || cleaned.length < 3) {
    return 'Wystąpił błąd. Spróbuj ponownie.';
  }
  
  return cleaned;
};

export function localizeApiError(message) {
  if (!message || typeof message !== "string") {
    return KNOWN_API_MESSAGES["Internal server error"];
  }
  const trimmed = message.trim();
  if (KNOWN_API_MESSAGES[trimmed]) {
    return KNOWN_API_MESSAGES[trimmed];
  }
  return sanitizeErrorMessage(trimmed);
}

/**
 * Odczyt komunikatu błędu z odpowiedzi fetch (JSON: error / message).
 */
export async function getFetchErrorMessage(response, fallback = "Wystąpił błąd. Spróbuj ponownie.") {
  if (!response) {
    return fallback;
  }
  try {
    const data = await response.json();
    const raw = data?.error || data?.message || data?.details;
    if (raw && typeof raw === "string") {
      return localizeApiError(raw);
    }
  } catch {
    /* ignore non-JSON body */
  }
  if (response.status === 401) {
    return "Sesja wygasła. Zaloguj się ponownie.";
  }
  if (response.status === 403) {
    return "Brak uprawnień do wykonania tej operacji.";
  }
  if (response.status >= 500) {
    return KNOWN_API_MESSAGES["Internal server error"];
  }
  return fallback;
}

/**
 * Funkcja do ekstrakcji komunikatu błędu z różnych typów błędów
 */
export const extractErrorMessage = (error) => {
  if (!error) {
    return 'Wystąpił nieoczekiwany błąd.';
  }

  // Jeśli error jest stringiem
  if (typeof error === "string") {
    return localizeApiError(error);
  }

  // Jeśli error ma właściwość message
  if (error.message) {
    return localizeApiError(error.message);
  }

  // Jeśli error ma właściwość error (z axios)
  if (error.error) {
    return sanitizeErrorMessage(error.error);
  }

  // Jeśli error ma response.data.message (z axios)
  if (error.response?.data?.message) {
    return sanitizeErrorMessage(error.response.data.message);
  }

  // Jeśli error ma response.data.error (z axios)
  if (error.response?.data?.error) {
    return sanitizeErrorMessage(error.response.data.error);
  }

  // Jeśli error ma response.statusText
  if (error.response?.statusText) {
    return sanitizeErrorMessage(error.response.statusText);
  }

  // Domyślny komunikat
  return "Wystąpił błąd. Spróbuj ponownie.";
};

/** Alias — ten sam komunikat co extractErrorMessage. */
export function getErrorMessage(error, fallback) {
  const msg = extractErrorMessage(error);
  if (msg === "Wystąpił błąd. Spróbuj ponownie." && fallback) {
    return fallback;
  }
  return msg;
}
